import React, { useEffect } from 'react';
import { get } from 'lodash-es';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { EdmundsEmptyDecorator } from 'site-modules/shared/pages/decorators/edmunds-empty';
import { auth } from 'site-modules/shared/components/profile/firebase-auth';

import { getStaticImageUrl } from 'client/utils/image-helpers';
import { Spinner } from 'site-modules/shared/components/spinner/spinner';
import { SmartSign } from 'site-modules/shared/components/profile/smart-sign/smart-sign';

import { getVanillaSSOSignatureString } from 'site-modules/shared/components/profile/idm/idm';
import { connectedProfileAuth } from 'site-modules/shared/components/profile/profile-auth';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { profileScreenMethods } from 'site-modules/shared/components/profile/profile-screen-methods';

import 'site-modules/shared/components/profile/profile.scss';
import './login-page.scss';

function LoginPageComponent({
  isAuthenticated = false,
  signOut,
  toggleSignModal,
  hasIdentifierEmail,
  hasProfileData,
  updateIdmData,
}) {
  const user = auth().currentUser;

  useEffect(() => {
    async function postMessage() {
      const vanillaSSOSignatureString = await getVanillaSSOSignatureString().catch(() => null);
      const dataToPost = {
        isAuthenticated: true,
        vanilla_sso: vanillaSSOSignatureString,
      };
      if (vanillaSSOSignatureString) {
        window.top.postMessage(dataToPost, '*');
      } else {
        // if no signature string then we want to sign out and display the form again
        signOut();
      }
    }

    if (user && hasProfileData) {
      if (hasIdentifierEmail) {
        // Verifies that data completed saving and profile has email then triggers post message.
        postMessage();
      } else {
        updateIdmData({
          identifiers: {
            email: [user.email],
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasIdentifierEmail, hasProfileData]);

  useEffect(() => {
    toggleSignModal();

    if (isAuthenticated) {
      signOut();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-tracking-parent="edm-entry-login-page">
      {isAuthenticated ? (
        <div className="login-page-spinner-container">
          <Spinner size={48} />
        </div>
      ) : (
        <div className="smart-sign p-1_5">
          <div className="edmunds-logo mt-0_75 mb-2">
            <img
              src={getStaticImageUrl('/images/logos/edmunds-logo-and-text-226x36-trans.png')}
              alt="edmunds logo and edmunds"
            />
          </div>
          <SmartSign />
        </div>
      )}
    </div>
  );
}

LoginPageComponent.propTypes = {
  isAuthenticated: PropTypes.bool,
  toggleSignModal: PropTypes.func.isRequired,
  signOut: PropTypes.func.isRequired,
  hasIdentifierEmail: PropTypes.bool.isRequired,
  hasProfileData: PropTypes.bool.isRequired,
  updateIdmData: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  hasProfileData: !!get(state, 'profile.data.idm'),
  hasIdentifierEmail: !!get(state, 'profile.data.idm.identifiers.email[0]'),
  isAuthenticated: get(state, 'profile.isAuthenticated'),
});

export const LoginPage = connect(mapStateToProps)(
  connectedProfileAuth(insiderMethods(profileScreenMethods(LoginPageComponent)), {
    renderWhenInitialized: true,
  })
);

export default pageDefinition({
  name: 'insider_login_page',
  category: 'insider_login_page',
  component: LoginPage,
  decorator: EdmundsEmptyDecorator,
  seo: {
    title: 'Edmunds Insider Membership Forums Sign In',
    description: 'Register with Edmunds Insider to receive full forums access such as creating posts and comments.',
    robots: {
      noFollow: true,
      noIndex: true,
    },
  },
});
